/**
 * The mock payment page.
 *
 * ⚠️ IT SAYS, ON THE PAGE, THAT NO MONEY MOVES — and it says it because THIS component is the one
 * that knows (CLAUDE.md §0.4). A banner in the application shell would be a banner somebody forgets
 * to remove the day a real provider lands, which is the exact failure the predecessor shipped twice:
 * a "served by a stub" notice left on a surface that had become real, telling a customer they were
 * in a sandbox while their configuration was durable.
 *
 * The notice reads `billing.real` from the server rather than assuming. When a real provider is
 * configured, this page is not reached at all — `startSubscription` sends the customer to the
 * provider's own hosted page, because a form on our origin that collects a card number puts this
 * application in PCI-DSS scope and nothing about this product is worth that.
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Button, Card, Notice, Price, useLang } from '@ksa/ui'
import { PRICE } from '@ksa/model'
import { ApiFailure, api } from '../api.ts'
import { useLoad } from '../useLoad.ts'

export function Pay() {
  const { t, lang } = useLang()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [session] = useLoad((signal) => api.session(signal), [])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)

  const ref = params.get('ref') ?? ''
  const siteId = params.get('site') ?? ''
  const returnTo = params.get('returnTo') ?? (siteId ? `/s/${siteId}` : '/sites')

  const split = PRICE.split()

  const confirm = async () => {
    setBusy(true)
    setFailure(null)
    try {
      await api.confirmSubscription(siteId, ref)
      navigate(returnTo)
    } catch (error) {
      setFailure(error instanceof ApiFailure ? error : null)
      setBusy(false)
    }
  }

  return (
    <div className="u-page u-page--narrow u-section u-stack">
      <h1 className="u-title">{t('Confirm your subscription', 'أكّد اشتراكك')}</h1>

      <Card raised>
        <div className="u-stack">
          <div className="u-row u-row--between">
            <span>{t('One published website', 'موقع واحد منشور')}</span>
            <Price showVat={false} inline />
          </div>

          {/*
            ⚠️ THE VAT IS SHOWN AS A SPLIT, NOT AS A FOOTNOTE. Saudi consumer prices are displayed
            inclusive of 15% VAT — that is the law, not a preference — and an invoice has to show the
            net and the tax separately. Computing it here from one constant means the receipt and the
            price can never disagree.
          */}
          <div className="u-stack u-stack--tight u-small u-soft" style={{ borderBlockStart: '1px solid var(--u-line)', paddingBlockStart: 'var(--u-s3)' }}>
            <div className="u-row u-row--between">
              <span>{t('Before VAT', 'قبل الضريبة')}</span>
              <span className="u-num">{PRICE.format(lang, split.net)}</span>
            </div>
            <div className="u-row u-row--between">
              <span>{t('VAT (15%)', 'ضريبة القيمة المضافة (١٥٪)')}</span>
              <span className="u-num">{PRICE.format(lang, split.vat)}</span>
            </div>
            <div className="u-row u-row--between" style={{ fontWeight: 600, color: 'var(--u-ink)' }}>
              <span>{t('Total per month', 'الإجمالي شهريًا')}</span>
              <span className="u-num">{PRICE.format(lang, split.gross)}</span>
            </div>
          </div>

          {session.state === 'ready' && !session.data.billing.real && (
            <Notice tone="warn" title={t('No card is charged', 'لن تُخصم أي بطاقة')}>
              {t(
                'Payments are not connected yet on this deployment. Pressing the button below marks this site as paid so you can try publishing — no money moves and no card details are taken.',
                'المدفوعات غير مفعّلة بعد في هذه النسخة. الضغط على الزر أدناه يجعل هذا الموقع مدفوعًا لتجربة النشر — لا تُحوَّل أي أموال ولا تُطلب بيانات بطاقة.',
              )}
            </Notice>
          )}

          {failure && <Notice tone="error">{lang === 'ar' ? failure.text.ar : failure.text.en}</Notice>}

          <div className="u-row">
            <Button busy={busy} disabled={!siteId || !ref} onClick={confirm}>
              {session.state === 'ready' && !session.data.billing.real
                ? t('Mark as paid and continue', 'اعتبره مدفوعًا وتابع')
                : /* ⚠️ THE AMOUNT COMES FROM `PRICE`, NOT FROM THE SENTENCE. It was typed into both
                     languages here — so the day the price changed, this button went on quoting the
                     old one, in the one place a customer is about to be charged. */
                  t(
                    `Pay ${PRICE.amountOnly('en')} SAR a month`,
                    `ادفع ${PRICE.amountOnly('ar')} ريال شهريًا`,
                  )}
            </Button>
            <Button variant="ghost" onClick={() => navigate(returnTo)}>
              {t('Not now', 'ليس الآن')}
            </Button>
          </div>

          <p className="u-tiny u-faint">
            {t(
              'Cancel any time from the site’s settings. Your site stays up until the month you paid for ends.',
              'ألغِ في أي وقت من إعدادات الموقع. يبقى موقعك يعمل حتى نهاية الشهر المدفوع.',
            )}
          </p>
        </div>
      </Card>
    </div>
  )
}
